import React, { useEffect, useState } from "react";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import Swal from "sweetalert2";
import MainLayout from "../../../components/layout/MainLayout";
import PROSAssessmentForm from "./PROSAssessmentForm";
import {
    fetchProsAssessment,
    updateProsAssessment,
    clearError,
} from "../../../store/slices/prosAssessmentsSlice";

const EditPROSAssessment = () => {
    const navigate = useNavigate();
    const { id } = useParams();
    const dispatch = useDispatch();
    const { state } = useLocation();
    const { caseId, patientId } = state || {};

    const { currentProsAssessment, isLoading, error } = useSelector(
        (state) => state.prosAssessments
    );

    const [formData, setFormData] = useState(null);
    const [validationErrors, setValidationErrors] = useState({});

    // === Fetch on Mount ===
    useEffect(() => {
        dispatch(clearError());
        dispatch(fetchProsAssessment(id));
    }, [dispatch, id]);

    // === Map Backend → Frontend ===
    useEffect(() => {
        if (currentProsAssessment) {
            const mapPart1 = () => {
                const areas = {};
                const areaKeys = [
                    "tellMeAboutYou",
                    "dailyRoutine",
                    "employment",
                    "learning",
                    "housing",
                    "healthyLifestyle",
                    "relationships",
                    "parenting",
                    "activities",
                ];

                // ✅ correct key name from API
                const part1Areas = currentProsAssessment?.part1_areas || [];

                areaKeys.forEach((key) => {
                    const area = part1Areas.find((a) => a.area_key === key) || {};
                    areas[key] = {
                        text: area.text || "",
                        wantToWork: area.want_to_work || "",
                    };
                });

                return areas;
            };

            const mapPart2 = () => {
                // ✅ correct key names from API
                const goals = (currentProsAssessment?.part2_goals || []).map((g, i) => ({
                    id: g.id || Date.now() + i,
                    text: g.text || "",
                }));

                const sections = {};
                const sectionKeys = [
                    "exploring_goal",
                    "imagining_change",
                    "skills_supports",
                    "overcoming_barriers",
                    "journey_support",
                ];
                const part2Sections = currentProsAssessment?.part2_sections || [];

                sectionKeys.forEach((key) => {
                    const section = part2Sections.find((s) => s.section_key === key) || {};
                    const camelKey = key
                        .replace("exploring_goal", "exploringGoal")
                        .replace("imagining_change", "imaginingChange")
                        .replace("skills_supports", "skillsSupports")
                        .replace("overcoming_barriers", "overcomingBarriers")
                        .replace("journey_support", "journeySupport");

                    sections[camelKey] = section.text || "";
                });

                return { goals, ...sections };
            };

            const mapPart3 = () => {
                const p3 = currentProsAssessment?.part3 || {};
                return {
                    practitionerSummary: p3.practitioner_summary || "",
                    practitionerRecommendations: p3.practitioner_recommendations || "",
                    checkedItems: p3.checked_items || {
                        lifeRoleDevelopment: false,
                        lifeRoleRefinement: false,
                        lifeRoleAcquisition: false,
                        lifeRoleRetention: false,
                    },
                };
            };

            // ✅ map all parts into formData
            setFormData({
                patientId: patientId || currentProsAssessment.patientId,
                caseId: caseId || currentProsAssessment.caseId,
                clientName: currentProsAssessment.client_name || "",
                effectiveDate: currentProsAssessment.effective_date
                    ? currentProsAssessment.effective_date.split("T")[0] // ✅ Fix date format
                    : "",
                part1: mapPart1(),
                part2: mapPart2(),
                part3: mapPart3(),
            });
        }
    }, [currentProsAssessment]);


    // === Validation ===
    const validate = () => {
        const errors = {};
        if (!formData?.clientName) errors.clientName = "Client Name is required.";
        if (!formData?.effectiveDate) errors.effectiveDate = "Date is required.";
        if (formData?.part2?.goals?.some((g) => !g.text))
            errors.goals = "All goals must have text.";
        setValidationErrors(errors);
        return Object.keys(errors).length === 0;
    };

    // === Payload Formatter ===
    const formatPayload = (data) => {
        const toNull = (val) => (val === "" ? null : val);

        return {
            patient_id: toNull(data.patientId),
            case_id: toNull(data.caseId),
            client_name: data.clientName,
            effective_date: data.effectiveDate,

            // Part 1
            part1: {
                tellMeAboutYou: {
                    text: toNull(data.part1.tellMeAboutYou.text),
                    want_to_work: toNull(data.part1.tellMeAboutYou.wantToWork),
                },
                dailyRoutine: {
                    text: toNull(data.part1.dailyRoutine.text),
                    want_to_work: toNull(data.part1.dailyRoutine.wantToWork),
                },
                employment: {
                    text: toNull(data.part1.employment.text),
                    want_to_work: toNull(data.part1.employment.wantToWork),
                },
                learning: {
                    text: toNull(data.part1.learning.text),
                    want_to_work: toNull(data.part1.learning.wantToWork),
                },
                housing: {
                    text: toNull(data.part1.housing.text),
                    want_to_work: toNull(data.part1.housing.wantToWork),
                },
                healthyLifestyle: {
                    text: toNull(data.part1.healthyLifestyle.text),
                    want_to_work: toNull(data.part1.healthyLifestyle.wantToWork),
                },
                relationships: {
                    text: toNull(data.part1.relationships.text),
                    want_to_work: toNull(data.part1.relationships.wantToWork),
                },
                parenting: {
                    text: toNull(data.part1.parenting.text),
                    want_to_work: toNull(data.part1.parenting.wantToWork),
                },
                activities: {
                    text: toNull(data.part1.activities.text),
                    want_to_work: toNull(data.part1.activities.wantToWork),
                },
            },

            // Part 2
            part2: {
                goals: data.part2.goals.map((g) => ({ text: g.text })),
                exploring_goal: toNull(data.part2.exploringGoal),
                imagining_change: toNull(data.part2.imaginingChange),
                skills_supports: toNull(data.part2.skillsSupports),
                overcoming_barriers: toNull(data.part2.overcomingBarriers),
                journey_support: toNull(data.part2.journeySupport),
            },

            // Part 3
            part3: {
                practitioner_summary: toNull(data.part3.practitionerSummary),
                practitioner_recommendations: toNull(data.part3.practitionerRecommendations),
                checked_items: data.part3.checkedItems,
            },
        };
    };

    // === Submit Handler ===
    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!validate()) {
            Swal.fire({
                title: "Validation Error",
                html: Object.values(validationErrors).join("<br>"),
                icon: "warning",
            });
            return;
        }

        const payload = formatPayload(formData);

        try {
            await dispatch(updateProsAssessment({ case_id: caseId, prosAssessmentData: payload })).unwrap();

            Swal.fire({
                title: "Updated!",
                text: "PROS Assessment updated successfully.",
                icon: "success",
                timer: 2000,
                showConfirmButton: false,
            });

            navigate(`/cases/${caseId}/profile`, { state: { patientId, caseId } });
        } catch (err) {
            const errorMsg =
                err?.errors
                    ? Object.values(err.errors).flat().join("\n")
                    : err || "Failed to update assessment.";

            Swal.fire({
                title: "Error",
                text: errorMsg,
                icon: "error",
            });
        }
    };

    // === Loading & Error States ===
    if (isLoading || !formData) {
        return (
            <MainLayout>
                <div className="container-xxl flex-grow-1 container-p-y">
                    <div className="card">
                        <div className="card-body text-center py-5">
                            <div className="spinner-border text-primary" role="status">
                                <span className="visually-hidden">Loading...</span>
                            </div>
                            <p className="mt-3">Loading PROS Assessment...</p>
                        </div>
                    </div>
                </div>
            </MainLayout>
        );
    }

    if (error) {
        return (
            <MainLayout>
                <div className="container-xxl flex-grow-1 container-p-y">
                    <div className="card">
                        <div className="card-body">
                            <div className="alert alert-danger">
                                <strong>Error:</strong> {error}
                            </div>
                        </div>
                    </div>
                </div>
            </MainLayout>
        );
    }

    return (
        <MainLayout>
            <div className="container-xxl flex-grow-1 container-p-y">
                <div className="card">
                    <div className="card-body">
                        {/* Validation Errors */}
                        {Object.keys(validationErrors).length > 0 && (
                            <div className="alert alert-warning">
                                {Object.values(validationErrors).map((err, i) => (
                                    <div key={i}>• {err}</div>
                                ))}
                            </div>
                        )}

                        <form onSubmit={handleSubmit}>
                            <PROSAssessmentForm
                                formData={formData}
                                setFormData={setFormData}
                                isLoading={isLoading}
                                handleSubmit={handleSubmit}
                            />
                        </form>
                    </div>
                </div>
            </div>
        </MainLayout>
    );
};

export default EditPROSAssessment;